"use client";

import React, { useState, useMemo, useEffect } from "react";
import { motion } from "framer-motion";
import { Swiper, SwiperSlide, useSwiper } from "swiper/react";
import { A11y, Autoplay, Pagination } from "swiper/modules";
import { Star, Quote, Calculator, Zap, CircleCheck as CheckCircle2, ChevronLeft, ChevronRight, Clock, MapPin, Sparkles, ShieldCheck, ArrowRight, Wrench } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/autoplay";
import "swiper/css/a11y";
type Language = 'es' | 'en' | 'eu' | 'fr';
const LANGUAGE_STORAGE_KEY = 'wvc_language';
const LANGUAGE_EVENT_NAME = 'wvc-language-change';
function useLanguage(): [Language, (lang: Language) => void] {
  const [language, setLanguageState] = useState<Language>(() => {
    if (typeof window === "undefined") return "es";
    try {
      const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY);
      if (saved === "es" || saved === "en" || saved === "eu" || saved === "fr") {
        return saved;
      }
    } catch {
      // localStorage may be disabled or restricted
    }
    return "es";
  });
  useEffect(() => {
    const handleLanguageChange = (e: Event) => {
      try {
        const detail = (e as CustomEvent<Language>).detail;
        if (detail === "es" || detail === "en" || detail === "eu" || detail === "fr") {
          setLanguageState(detail);
        } else {
          const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY);
          if (saved === "es" || saved === "en" || saved === "eu" || saved === "fr") {
            setLanguageState(saved);
          }
        }
      } catch {
        // ignore errors
      }
    };
    window.addEventListener(LANGUAGE_EVENT_NAME, handleLanguageChange);
    window.addEventListener("storage", handleLanguageChange);
    return () => {
      window.removeEventListener(LANGUAGE_EVENT_NAME, handleLanguageChange);
      window.removeEventListener("storage", handleLanguageChange);
    };
  }, []);
  const setLanguage = (newLang: Language) => {
    try {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, newLang);
      setLanguageState(newLang);
      window.dispatchEvent(new CustomEvent(LANGUAGE_EVENT_NAME, {
        detail: newLang
      }));
    } catch {
      setLanguageState(newLang);
    }
  };
  return [language, setLanguage];
}
const reviewImages = [{
  id: 1,
  name: "Eguz",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/trabajo-cuadro.jpg",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/trabajo-cuadro.jpg-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/trabajo-cuadro.jpg-wvc-sizes",
    alt: "Instalación de iluminación LED arquitectónica en Bilbao"
  }
}, {
  id: 2,
  name: "Fer Het",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/trabajo-reforma.jpg",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/trabajo-reforma.jpg-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/trabajo-reforma.jpg-wvc-sizes",
    alt: "Cuadro eléctrico de alta precisión e instalación limpia"
  }
}, {
  id: 3,
  name: "I B",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/trabajo-cableado.jpg",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/trabajo-cableado.jpg-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/trabajo-cableado.jpg-wvc-sizes",
    alt: "Integración oficial de sistema domótico Loxone"
  }
}, {
  id: 4,
  name: "María Rey",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/equipo.jpg",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/equipo.jpg-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/equipo.jpg-wvc-sizes",
    alt: "Punto de recarga para vehículo eléctrico e instalaciones"
  }
}, {
  id: 5,
  name: "Jesus Garcia",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/trabajo-cuadro.jpg",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/trabajo-cuadro.jpg-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/trabajo-cuadro.jpg-wvc-sizes",
    alt: "Acabado de iluminación impecable en reforma residencial"
  }
}, {
  id: 6,
  name: "Porti",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/trabajo-cableado.jpg",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/trabajo-cableado.jpg-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1825298/img/trabajo-cableado.jpg-wvc-sizes",
    alt: "Instalación y mantenimiento de alta rapidez"
  }
}];
const needConfigs = [{
  id: "domotica",
  baseRange: [2500, 12000]
}, {
  id: "reforma",
  baseRange: [3200, 9500]
}, {
  id: "cuadro",
  baseRange: [850, 2400]
}, {
  id: "iluminacion",
  baseRange: [650, 2800]
}, {
  id: "recarga",
  baseRange: [950, 2200]
}, {
  id: "boletin",
  baseRange: [220, 550]
}, {
  id: "averia",
  baseRange: [120, 480]
}];
const locationConfigs = [{
  id: "vivienda",
  factor: 1.0
}, {
  id: "local",
  factor: 1.35
}, {
  id: "comunidad",
  factor: 1.6
}, {
  id: "nave",
  factor: 2.1
}];
const timeframeConfigs = [{
  id: "sin-prisa",
  factor: 0.95
}, {
  id: "esta-semana",
  factor: 1.0
}, {
  id: "urgente",
  factor: 1.25
}];
const translations = {
  es: {
    badge: "Opiniones 100% Verificadas",
    heading: "Reseñas de verdad, trabajo de verdad",
    paragraph: "Sin rodeos ni trampa. Experiencias reales de clientes en Bilbao, alrededores y todo el País Vasco que han confiado sus reformas eléctricas y proyectos Loxone en nuestras manos.",
    carouselTitle: "Lo que dicen de Carlos y el equipo",
    carouselSubtitle: "4.9/5 valoración media en instalaciones eléctricas y domótica",
    prevReview: "Reseña anterior",
    nextReview: "Siguiente reseña",
    certifiedNote: "Servicio certificado e integración oficial Loxone en Bizkaia y todo el País Vasco",
    reviews: [{
      id: 1,
      role: "Propietario · Bilbao",
      tag: "Iluminación LED",
      quote: "Contentísimos con la profesionalidad de Karlos. Nos gestionó y dio solución a la instalación de luces tanto en techos bajados como con lámparas de superficie, para armarios, plafón... Y por supuesto, calidad-precio buenísima.",
      rating: 5
    }, {
      id: 2,
      role: "Cliente · Bizkaia",
      tag: "Reforma Eléctrica",
      quote: "Gran profesional, muy fino y muy limpio. Teníamos dudas por una horrible experiencia anterior con otro electricista y desde el primer instante vimos que no íbamos a tener ningún problema. Muy contentos con Karlos, se lo recomendaría a cualquier persona.",
      rating: 5
    }, {
      id: 3,
      role: "Cliente · Getxo",
      tag: "Cuadro Eléctrico",
      quote: "Muchas gracias por todo, nadie me había dado un presupuesto tan bueno y con tan buenos resultados. Muy recomendable.",
      rating: 5
    }, {
      id: 4,
      role: "Reforma Piso Segunda Mano",
      tag: "Cableado Completo",
      quote: "Decidí cambiar la instalación eléctrica de mi piso de segunda mano una vez hecha la mudanza. A pesar de las dificultades (tuvieron que cambiar de sitio todos los muebles, muchos tabiques eran de mala calidad...), supieron salvarlas, llevando a cabo un buen trabajo. Muy buena calidad-precio. Muy contenta con el resultado.",
      rating: 5
    }, {
      id: 5,
      role: "Cliente · Bilbao",
      tag: "Servicio Técnico",
      quote: "Muy buen trato y un trabajo impecable",
      rating: 5
    }, {
      id: 6,
      role: "Cliente · Barakaldo",
      tag: "Urgencia 24h",
      quote: "Trabajo rápido y limpio, todo muy bien",
      rating: 5
    }],
    estimatorBadge: "Estimación en 30 segundos",
    estimatorHeading: "¿Cuánto puede costar tu proyecto?",
    estimatorParagraph: "Elige tres opciones y obtén una horquilla orientativa al momento. Sin registro.",
    step1: "¿Qué necesitas?",
    step2: "¿Dónde?",
    step3: "¿Para cuándo?",
    needs: {
      domotica: "Domótica Loxone",
      reforma: "Reforma eléctrica integral",
      cuadro: "Cuadro eléctrico",
      iluminacion: "Iluminación / LED",
      recarga: "Punto de recarga VE",
      boletin: "Boletín / legalización",
      averia: "Avería / urgencia"
    },
    locations: {
      vivienda: "Vivienda",
      local: "Local comercial",
      comunidad: "Comunidad de vecinos",
      nave: "Nave / industria"
    },
    timeframes: {
      "sin-prisa": "Sin prisa (1 mes)",
      "esta-semana": "Esta semana",
      urgente: "Urgente 24h"
    },
    estTitle: "Estimación orientativa",
    serviceLabel: "Servicio:",
    disclaimer: "*Impuestos no incluidos. Precio sujeto a inspección técnica final previa.",
    bullet1: "Respuesta técnica en menos de 2 horas",
    bullet2: "Instaladores autorizados con garantía",
    whatsappBtn: "Solicitar presupuesto por WhatsApp",
    emergencyLink: "¿Urgencia? Llama al 662 49 88 28"
  },
  en: {
    badge: "100% Verified Reviews",
    heading: "Real reviews, real work",
    paragraph: "No gimmicks or catches. Real experiences from clients in Bilbao, surroundings and across the Basque Country who trusted their electrical renovations and Loxone projects to us.",
    carouselTitle: "What people say about Carlos and the team",
    carouselSubtitle: "4.9/5 average rating in electrical installations and home automation",
    prevReview: "Previous review",
    nextReview: "Next review",
    certifiedNote: "Certified service and official Loxone integration in Bizkaia and across the Basque Country",
    reviews: [{
      id: 1,
      role: "Homeowner · Bilbao",
      tag: "LED Lighting",
      quote: "Delighted with Karlos's professionalism. He organized and solved the lighting installation in both dropped ceilings and surface fixtures, for closets, ceiling panels... And of course, great value for money.",
      rating: 5
    }, {
      id: 2,
      role: "Client · Bizkaia",
      tag: "Electrical Renovation",
      quote: "Great professional, very meticulous and very clean. We were hesitant due to an awful past experience with another electrician, but from day one we knew there wouldn't be any issue. Extremely satisfied with Karlos, I'd recommend him to anyone.",
      rating: 5
    }, {
      id: 3,
      role: "Client · Getxo",
      tag: "Electrical Panel",
      quote: "Thank you very much for everything, nobody had offered such a great quote with such outstanding results. Highly recommended.",
      rating: 5
    }, {
      id: 4,
      role: "Apartment Renovation",
      tag: "Full Rewiring",
      quote: "I decided to replace the electrical installation of my newly bought apartment after moving in. Despite the complications (moving all furniture around, fragile partitions...), they resolved everything and delivered high-quality work. Great value. Very happy with the result.",
      rating: 5
    }, {
      id: 5,
      role: "Client · Bilbao",
      tag: "Technical Service",
      quote: "Excellent treatment and immaculate craftsmanship",
      rating: 5
    }, {
      id: 6,
      role: "Client · Barakaldo",
      tag: "24/7 Emergency",
      quote: "Fast, clean work, everything turned out great",
      rating: 5
    }],
    estimatorBadge: "30-Second Estimate",
    estimatorHeading: "How much could your project cost?",
    estimatorParagraph: "Choose three options and get an instant indicative range. No sign-up required.",
    step1: "What do you need?",
    step2: "Where?",
    step3: "When?",
    needs: {
      domotica: "Loxone Home Automation",
      reforma: "Full electrical renovation",
      cuadro: "Electrical panel",
      iluminacion: "Lighting / LED",
      recarga: "EV charging station",
      boletin: "Certificate / compliance",
      averia: "Fault / emergency"
    },
    locations: {
      vivienda: "Home / Residential",
      local: "Commercial premises",
      comunidad: "Residential community",
      nave: "Warehouse / Industrial"
    },
    timeframes: {
      "sin-prisa": "No rush (1 month)",
      "esta-semana": "This week",
      urgente: "Urgent 24h"
    },
    estTitle: "Indicative estimate",
    serviceLabel: "Service:",
    disclaimer: "*Taxes not included. Price subject to final prior technical inspection.",
    bullet1: "Technical response in under 2 hours",
    bullet2: "Certified installers with guarantee",
    whatsappBtn: "Request a quote via WhatsApp",
    emergencyLink: "Emergency? Call 662 49 88 28"
  },
  eu: {
    badge: "%100 Egiaztatutako Iritziak",
    heading: "Benetako iritziak, benetako lana",
    paragraph: "Trikimailurik eta itzulingururik gabe. Bilbon, inguruetan eta Euskal Herri osoan beren erreforma elektrikoak eta Loxone proiektuak gure esku utzi dituzten bezeroen esperientzia errealak.",
    carouselTitle: "Carlos eta taldeari buruz esaten dutena",
    carouselSubtitle: "4.9/5 batez besteko balorazioa instalazio elektrikoetan eta domotikan",
    prevReview: "Aurreko iritzia",
    nextReview: "Hurrengo iritzia",
    certifiedNote: "Zerbitzu ziurtatua eta Loxone integrazio ofiziala Bizkaian eta Euskal Herri osoan",
    reviews: [{
      id: 1,
      role: "Jabea · Bilbo",
      tag: "LED Argiztapena",
      quote: "Biziki pozik Karlosen profesionaltasunarekin. Sabaian zein gainazaleko lanparatan argiztapen instalazioari irtenbide ezin hobea eman zion, armairuetarako, plafonetarako... Eta noski, kalitate-prezio aparta.",
      rating: 5
    }, {
      id: 2,
      role: "Bezeroa · Bizkaia",
      tag: "Erreforma Elektrikoa",
      quote: "Profesional bikaina, oso txukuna eta garbia. Beste elektrizista batekin izandako esperientzia kaxkarraren ondorioz zalantzak genituen, baina hasieratik argi ikusi genuen ez zela arazorik egongo. Oso pozik Karlosekin, edonori gomendatuko nioke.",
      rating: 5
    }, {
      id: 3,
      role: "Bezeroa · Getxo",
      tag: "Koadro Elektrikoa",
      quote: "Eskerrik asko guztiagatik, inork ez zidan eman hain aurrekontu onik eta hain emaitza bikainekin. Biziki gomendagarria.",
      rating: 5
    }, {
      id: 4,
      role: "Etxebizitzaren Erreforma",
      tag: "Kableatu Osoa",
      quote: "Etxez aldatu ondoren bigarren eskuko nire etxeko instalazio elektrikoa berritzea erabaki nuen. Zailtasunak zailtasun (altzari guztiak lekuz aldatu behar izan zituzten...), primeran gainditu zituzten, lan bikaina eginez. Kalitate-prezio ezin hobea.",
      rating: 5
    }, {
      id: 5,
      role: "Bezeroa · Bilbo",
      tag: "Zerbitzu Teknikoa",
      quote: "Tratu ezin hobea eta lan akasgabea",
      rating: 5
    }, {
      id: 6,
      role: "Bezeroa · Barakaldo",
      tag: "24h Larrialdia",
      quote: "Lan azkarra eta garbia, dena oso ondo",
      rating: 5
    }],
    estimatorBadge: "Zenbatespena 30 segundotan",
    estimatorHeading: "Zenbat kosta daiteke zure proiektua?",
    estimatorParagraph: "Hautatu hiru aukera eta jaso berehalako tarte orientagarria. Erregistratu gabe.",
    step1: "Zer behar duzu?",
    step2: "Non?",
    step3: "Noizko?",
    needs: {
      domotica: "Loxone Domotika",
      reforma: "Erreforma elektriko integrala",
      cuadro: "Koadro elektrikoa",
      iluminacion: "Argiztapena / LED",
      recarga: "Ibilgailu elektrikoaren kargagunea",
      boletin: "Buletina / legeztatzea",
      averia: "Hondatzea / larrialdia"
    },
    locations: {
      vivienda: "Etxebizitza",
      local: "Merkataritza lokala",
      comunidad: "Bizilagunen komunitatea",
      nave: "Pabiloia / industria"
    },
    timeframes: {
      "sin-prisa": "Presarik gabe (hilabete 1)",
      "esta-semana": "Aste honetan",
      urgente: "Premiazkoa 24h"
    },
    estTitle: "Zenbatespen orientagarria",
    serviceLabel: "Zerbitzua:",
    disclaimer: "*Zergak ez daude barne. Azken ikuskapen teknikoaren araberako prezioa.",
    bullet1: "Erantzun teknikoa 2 ordu baino gutxiagoan",
    bullet2: "Instalatzaile baimenduak bermearekin",
    whatsappBtn: "Eskatu aurrekontua WhatsApp bidez",
    emergencyLink: "Larrialdia? Deitu 662 49 88 28 zenbakira"
  },
  fr: {
    badge: "Avis 100% Vérifiés",
    heading: "De vrais avis, du vrai travail",
    paragraph: "Sans détour ni artifice. Les retours réels de nos clients à Bilbao, dans les environs et dans tout le Pays Basque qui nous ont confié leurs rénovations électriques et projets Loxone.",
    carouselTitle: "Ce qu'on dit de Carlos et de l'équipe",
    carouselSubtitle: "4.9/5 note moyenne en installations électriques et domotique",
    prevReview: "Avis précédent",
    nextReview: "Avis suivant",
    certifiedNote: "Service certifié et intégration officielle Loxone en Biscaye et dans tout le Pays Basque",
    reviews: [{
      id: 1,
      role: "Propriétaire · Bilbao",
      tag: "Éclairage LED",
      quote: "Ravis du professionnalisme de Karlos. Il a géré et résolu l'installation des luminaires tant en faux plafonds qu'en appliques, pour les placards, plafonniers... Et bien sûr, un rapport qualité-prix remarquable.",
      rating: 5
    }, {
      id: 2,
      role: "Client · Biscaye",
      tag: "Rénovation Électrique",
      quote: "Grand professionnel, très méticuleux et très propre. Nous hésitions à cause d'une très mauvaise expérience antérieure avec un autre électricien, et dès le départ nous avons su qu'il n'y aurait aucun souci. Très satisfaits de Karlos, je le recommande vivement.",
      rating: 5
    }, {
      id: 3,
      role: "Client · Getxo",
      tag: "Tableau Électrique",
      quote: "Merci beaucoup pour tout, personne ne m'avait proposé un si bon devis avec d'aussi bons résultats. Hautement recommandé.",
      rating: 5
    }, {
      id: 4,
      role: "Rénovation d'Appartement",
      tag: "Câblage Complet",
      quote: "J'ai décidé de refaire l'installation électrique de mon appartement après le déménagement. Malgré les imprévus (déplacer tous les meubles, cloisons fragiles...), ils ont tout surmonté avec un travail de grande qualité. Très satisfaite du résultat.",
      rating: 5
    }, {
      id: 5,
      role: "Client · Bilbao",
      tag: "Service Technique",
      quote: "Excellent contact et travail impeccable",
      rating: 5
    }, {
      id: 6,
      role: "Client · Barakaldo",
      tag: "Urgence 24h",
      quote: "Travail rapide et propre, tout s'est très bien passé",
      rating: 5
    }],
    estimatorBadge: "Estimation en 30 secondes",
    estimatorHeading: "Combien peut coûter votre projet ?",
    estimatorParagraph: "Sélectionnez trois options et obtenez une fourchette indicative instantanée. Sans inscription.",
    step1: "De quoi avez-vous besoin ?",
    step2: "Où ?",
    step3: "Pour quand ?",
    needs: {
      domotica: "Domotique Loxone",
      reforma: "Rénovation électrique intégrale",
      cuadro: "Tableau électrique",
      iluminacion: "Éclairage / LED",
      recarga: "Borne de recharge VE",
      boletin: "Certificat de conformité",
      averia: "Panne / urgence"
    },
    locations: {
      vivienda: "Logement",
      local: "Local commercial",
      comunidad: "Copropriété",
      nave: "Entrepôt / industrie"
    },
    timeframes: {
      "sin-prisa": "Sans urgence (1 mois)",
      "esta-semana": "Cette semaine",
      urgente: "Urgent 24h"
    },
    estTitle: "Estimation indicative",
    serviceLabel: "Service :",
    disclaimer: "*Taxes non comprises. Tarif sous réserve d'inspection technique préalable.",
    bullet1: "Réponse technique en moins de 2 heures",
    bullet2: "Installateurs agréés avec garantie",
    whatsappBtn: "Demander un devis via WhatsApp",
    emergencyLink: "Urgence ? Appelez le 662 49 88 28"
  }
};
function CustomSwiperControls({
  prevAria,
  nextAria
}: {
  prevAria: string;
  nextAria: string;
}) {
  const swiper = useSwiper();
  return <div className="flex items-center gap-2">
      <Button variant="outline" size="icon" onClick={() => swiper.slidePrev()} aria-label={prevAria} className="size-10 rounded-lg border-white/10 bg-secondary hover:bg-accent hover:border-primary/40 text-foreground transition-all duration-200">
        <ChevronLeft className="size-5 text-primary" />
      </Button>
      <Button variant="outline" size="icon" onClick={() => swiper.slideNext()} aria-label={nextAria} className="size-10 rounded-lg border-white/10 bg-secondary hover:bg-accent hover:border-primary/40 text-foreground transition-all duration-200">
        <ChevronRight className="size-5 text-primary" />
      </Button>
    </div>;
}
export default function Reviews() {
  const [language] = useLanguage();
  const t = translations[language as keyof typeof translations] || translations.es;
  const [selectedNeed, setSelectedNeed] = useState<string>("reforma");
  const [selectedLocation, setSelectedLocation] = useState<string>("vivienda");
  const [selectedTimeframe, setSelectedTimeframe] = useState<string>("esta-semana");
  const [reduceMotion, setReduceMotion] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduceMotion(mq.matches);
    const onChange = () => setReduceMotion(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  const localizedReviews = useMemo(() => {
    return reviewImages.map((imgItem, idx) => {
      const textItem = t.reviews[idx] || translations.es.reviews[idx];
      return {
        ...imgItem,
        ...textItem
      };
    });
  }, [t]);
  const localizedNeeds = useMemo(() => {
    return needConfigs.map(n => ({
      ...n,
      label: t.needs[n.id as keyof typeof t.needs] || n.id
    }));
  }, [t]);
  const localizedLocations = useMemo(() => {
    return locationConfigs.map(l => ({
      ...l,
      label: t.locations[l.id as keyof typeof t.locations] || l.id
    }));
  }, [t]);
  const localizedTimeframes = useMemo(() => {
    return timeframeConfigs.map(time => ({
      ...time,
      label: t.timeframes[time.id as keyof typeof t.timeframes] || time.id
    }));
  }, [t]);
  const priceEstimate = useMemo(() => {
    const needObj = needConfigs.find(n => n.id === selectedNeed) || needConfigs[1];
    const locObj = locationConfigs.find(l => l.id === selectedLocation) || locationConfigs[0];
    const timeObj = timeframeConfigs.find(tm => tm.id === selectedTimeframe) || timeframeConfigs[1];
    const min = Math.round(needObj.baseRange[0] * locObj.factor * timeObj.factor);
    const max = Math.round(needObj.baseRange[1] * locObj.factor * timeObj.factor);
    const formatter = new Intl.NumberFormat(language === "en" ? "en-US" : language === "fr" ? "fr-FR" : "es-ES");
    return `${formatter.format(min)} - ${formatter.format(max)} €`;
  }, [selectedNeed, selectedLocation, selectedTimeframe, language]);
  const selectedNeedLabel = useMemo(() => {
    return localizedNeeds.find(n => n.id === selectedNeed)?.label || "";
  }, [localizedNeeds, selectedNeed]);
  return <section id="reseñas-de-verdad-trabajo-de-verdad" data-nav="dark" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      {/* Structural background decoration */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
        <div className="absolute -top-40 -right-40 size-[32rem] rounded-full bg-primary/5 blur-3xl" />
        <div className="absolute -bottom-40 -left-40 size-[32rem] rounded-full bg-secondary/20 blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(59,130,246,0.03),transparent_70%)]" />
      </div>

      <div className="relative mx-auto max-w-[1904px] px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="mx-auto max-w-3xl text-center mb-16 lg:mb-20">
          <div className="inline-flex items-center gap-2 rounded-full bg-secondary/80 border border-primary/20 px-4 py-1.5 mb-4">
            <Sparkles className="size-4 text-primary" />
            <span className="font-mono text-xs font-semibold tracking-wider text-primary uppercase">
              {t.badge}
            </span>
          </div>

          <h2 className="font-sans text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-foreground leading-tight">
            {t.heading}
          </h2>

          <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
            {t.paragraph}
          </p>
        </motion.div>

        {/* Testimonials Carousel Section */}
        <div className="mb-24">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div>
              <h3 className="font-sans text-xl sm:text-2xl font-bold text-foreground flex items-center gap-2">
                <Quote className="size-6 text-primary fill-primary/20" />
                {t.carouselTitle}
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                {t.carouselSubtitle}
              </p>
            </div>
            <div className="hidden sm:block">
              {/* Swiper controls bound via child component inside Swiper */}
            </div>
          </div>

          <Swiper modules={[Pagination, Autoplay, A11y]} spaceBetween={24} slidesPerView={1} breakpoints={{
          640: {
            slidesPerView: 1.2
          },
          768: {
            slidesPerView: 2
          },
          1280: {
            slidesPerView: 3
          }
        }} loop autoplay={reduceMotion ? false : {
          delay: 5000,
          disableOnInteraction: false,
          pauseOnMouseEnter: true
        }} a11y={{
          prevSlideMessage: t.prevReview,
          nextSlideMessage: t.nextReview
        }} pagination={{
          clickable: true,
          bulletClass: "inline-block size-2 rounded-full bg-muted-foreground/30 mx-1 cursor-pointer transition-all duration-200",
          bulletActiveClass: "!bg-primary !w-6"
        }} className="!pb-14">
            {localizedReviews.map((rev, i) => <SwiperSlide key={rev.id} data-index={i} className="h-auto">
                <Card className="h-full border border-border/80 bg-card/90 backdrop-blur-sm rounded-xl p-6 sm:p-7 shadow-xl hover:border-primary/40 transition-[border-color,box-shadow] duration-300 flex flex-col justify-between">
                  <CardContent className="p-0 space-y-5 flex-1 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center justify-between gap-2 mb-3">
                        <div className="flex items-center gap-1">
                          {Array.from({
                        length: rev.rating
                      }).map((_, sIdx) => <Star key={sIdx} className="size-4 fill-primary text-primary" />)}
                        </div>
                        
                      </div>

                      <blockquote className="text-foreground/90 text-sm sm:text-base leading-relaxed italic relative">
                        &ldquo;{rev.quote}&rdquo;
                      </blockquote>
                    </div>

                    
                  </CardContent>
                </Card>
              </SwiperSlide>)}

            <div className="mt-6 flex justify-between items-center">
              <div className="text-xs text-muted-foreground flex items-center gap-2">
                <ShieldCheck className="size-4 text-primary" />
                {t.certifiedNote}
              </div>
              <CustomSwiperControls prevAria={t.prevReview} nextAria={t.nextReview} />
            </div>
          </Swiper>
        </div>

        {/* 30-Second Project Estimator Section */}
        <div className="pt-8 border-t border-border/60">
          <div className="max-w-5xl mx-auto rounded-2xl border border-primary/20 bg-card/95 p-6 sm:p-10 shadow-2xl relative overflow-hidden">
            <div className="pointer-events-none absolute -right-20 -bottom-20 size-80 rounded-full bg-primary/10 blur-3xl" aria-hidden="true" />

            {/* Estimator Header */}
            <div className="text-center max-w-2xl mx-auto mb-10">
              <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3.5 py-1 text-primary text-xs font-mono font-semibold uppercase tracking-wider mb-3">
                <Clock className="size-3.5" /> {t.estimatorBadge}
              </div>
              <h3 className="font-sans text-2xl sm:text-3xl font-extrabold text-foreground">
                {t.estimatorHeading}
              </h3>
              <p className="mt-2 text-sm sm:text-base text-muted-foreground">
                {t.estimatorParagraph}
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
              {/* Form Options Column */}
              <div className="lg:col-span-8 space-y-8">
                {/* Step 1 */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                    <span className="size-6 rounded-md bg-primary text-primary-foreground font-mono text-xs flex items-center justify-center font-bold">
                      1
                    </span>
                    <span>{t.step1}</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {localizedNeeds.map((opt, i) => {
                    const isSelected = selectedNeed === opt.id;
                    return <Button key={opt.id} type="button" variant="outline" data-index={i} onClick={() => setSelectedNeed(opt.id)} className={`h-auto py-2.5 px-3 justify-start text-xs font-medium rounded-lg border transition-all text-left whitespace-normal ${isSelected ? "bg-primary/15 border-primary text-primary shadow-xs" : "bg-secondary/60 border-border hover:border-primary/40 text-foreground"}`}>
                          <Zap className={`size-3.5 mr-1.5 shrink-0 ${isSelected ? "text-primary" : "text-muted-foreground"}`} />
                          <span className="truncate">{opt.label}</span>
                        </Button>;
                  })}
                  </div>
                </div>

                {/* Step 2 */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                    <span className="size-6 rounded-md bg-primary text-primary-foreground font-mono text-xs flex items-center justify-center font-bold">
                      2
                    </span>
                    <span>{t.step2}</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {localizedLocations.map((loc, i) => {
                    const isSelected = selectedLocation === loc.id;
                    return <Button key={loc.id} type="button" variant="outline" data-index={i} onClick={() => setSelectedLocation(loc.id)} className={`h-auto py-2.5 px-3 justify-center text-xs font-medium rounded-lg border transition-all ${isSelected ? "bg-primary/15 border-primary text-primary shadow-xs" : "bg-secondary/60 border-border hover:border-primary/40 text-foreground"}`}>
                          <MapPin className={`size-3.5 mr-1.5 shrink-0 ${isSelected ? "text-primary" : "text-muted-foreground"}`} />
                          <span>{loc.label}</span>
                        </Button>;
                  })}
                  </div>
                </div>

                {/* Step 3 */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                    <span className="size-6 rounded-md bg-primary text-primary-foreground font-mono text-xs flex items-center justify-center font-bold">
                      3
                    </span>
                    <span>{t.step3}</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {localizedTimeframes.map((time, i) => {
                    const isSelected = selectedTimeframe === time.id;
                    return <Button key={time.id} type="button" variant="outline" data-index={i} onClick={() => setSelectedTimeframe(time.id)} className={`h-auto py-2.5 px-3 justify-center text-xs font-medium rounded-lg border transition-all ${isSelected ? "bg-primary/15 border-primary text-primary shadow-xs" : "bg-secondary/60 border-border hover:border-primary/40 text-foreground"}`}>
                          <Clock className={`size-3.5 mr-1.5 shrink-0 ${isSelected ? "text-primary" : "text-muted-foreground"}`} />
                          <span>{time.label}</span>
                        </Button>;
                  })}
                  </div>
                </div>
              </div>

              {/* Estimate Calculation Result Column */}
              <div className="lg:col-span-4 bg-secondary/80 border border-primary/20 rounded-xl p-6 flex flex-col justify-between space-y-6 h-full">
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground uppercase tracking-wider">
                    <Calculator className="size-4 text-primary" />
                    <span>{t.estTitle}</span>
                  </div>

                  <div>
                    <div className="text-xs text-muted-foreground mb-1">
                      {t.serviceLabel} <span className="text-foreground font-medium">{selectedNeedLabel}</span>
                    </div>
                    <div className="font-mono text-3xl sm:text-4xl font-extrabold text-primary tracking-tight">
                      {priceEstimate}
                    </div>
                    <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                      {t.disclaimer}
                    </p>
                  </div>

                  <div className="space-y-2 pt-2 border-t border-border/60 text-xs text-foreground/80">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                      <span>{t.bullet1}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="size-3.5 text-primary shrink-0" />
                      <span>{t.bullet2}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-3 pt-2">
                  <Button asChild className="w-full bg-[#25D366] hover:bg-[#25D366]/90 text-white font-bold h-12 rounded-lg text-sm shadow-lg shadow-primary/20">
                    <Link to="https://wa.me/34662498828" newTab>
                      <span>{t.whatsappBtn}</span>
                      <ArrowRight className="size-4 ml-1" />
                    </Link>
                  </Button>

                  <div className="text-center">
                    <a href="tel:662498828" className="text-xs font-mono text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-1.5">
                      <Wrench className="size-3 text-primary" />
                      {t.emergencyLink}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}